import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { animate, group, query, style, transition, trigger } from '@angular/animations';
import { TranslateService } from '@ngx-translate/core';
import { IFaq } from '@shared/interfaces';
import { FaqService } from '../../services/faq.service';
import { Subscription } from 'rxjs';
import { FileService } from '../../services/file.service';
import { IAttachment } from '../../interfaces/mailing/IAttachment';
import { IS_PRODUCT_SAFETY_PROJECT, IS_TRS_PROJECT } from '../../../../current-project';

@Component({
    selector: 'app-faq',
    templateUrl: './faq.component.html',
    styleUrls: ['./faq.component.scss'],
    animations: [
        trigger('slide', [
            transition(
                ':increment',
                group([
                    query(':enter', [
                        style({
                            transform: 'translateX(100%)'
                        }),
                        animate(
                            500,
                            style({
                                transform: 'translateX(0)'
                            })
                        )
                    ])
                ])
            ),
            transition(
                ':decrement',
                group([
                    query(':enter', [
                        style({
                            transform: 'translateX(-100%)'
                        }),
                        animate(
                            500,
                            style({
                                transform: 'translateX(0)'
                            })
                        )
                    ])
                ])
            )
        ]),
        trigger('slideTopic', [
            transition(':enter', [
                style({
                    transform: 'translateX(100%)'
                }),
                animate(
                    500,
                    style({
                        transform: 'translateX(0)'
                    })
                )
            ])
        ])
    ],
    standalone: false
})
export class FaqComponent implements OnInit, OnDestroy {
    IS_TRS_PROJECT = IS_TRS_PROJECT;
    IS_PRODUCT_SAFETY_PROJECT = IS_PRODUCT_SAFETY_PROJECT;

    private tourItems: IFaq = {
        translationKey: 'interactiveTour',
        sortIndex: 0
    };

    subscription: Subscription;
    show = false;
    historyStack: IFaq[] = [];
    historyKeys: string[] = [];
    level = 0;
    faqItems: IFaq[];
    currentFaqItem: IFaq;
    currentLanguage: string;
    visibleItems: IFaq[];
    private initialFaqHeadline: string;
    @ViewChild('videoElement') videoElement: ElementRef<HTMLVideoElement>;

    constructor(
        private translate: TranslateService,
        private faqService: FaqService,
        private fileService: FileService
    ) {}

    async ngOnInit() {
        this.subscription = this.faqService.faqController.subscribe(e => (this.show = e));
        await this.loadData();
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    toggle() {
        this.show = !this.show;
        this.faqService.toggleFaq();

        document.body.style.overflowY = this.show ? 'hidden' : 'auto';

        if (!this.show && this.videoElement) {
            this.videoElement.nativeElement.pause();
        }
    }

    getCurrentLanguage(): string {
        return this.translate.currentLang;
    }

    selectItem(item: IFaq) {
        if (!item.event) {
            this.level++;
            this.historyStack.push(this.currentFaqItem);
            this.historyKeys.push(item.translationKey);
            this.currentFaqItem = item;
            this.visibleItems = this.currentFaqItem.items?.filter(e => !e.isVisible || e.isVisible());
        } else {
            item.event();
            this.toggle();
        }
    }

    goBack() {
        if (this.historyStack.length > 0) {
            this.currentFaqItem = this.historyStack.pop();
            this.visibleItems = this.currentFaqItem.items?.filter(e => !e.isVisible || e.isVisible());
            this.historyKeys.pop();

            this.level--;
        }
    }

    getTitle(item: IFaq): string | null {
        return item.translationKey
            ? this.translate.instant(
                  `faq.${this.historyKeys?.length ? this.historyKeys.join('.') + '.' : ''}${item.translationKey}.title`
              )
            : item.title
              ? item.title[this.translate.currentLang] || item.title
              : (item.title as string);
    }

    getContent(item: IFaq): string {
        return item?.translationKey
            ? this.translate.instant(
                  `faq.${this.historyKeys?.length ? this.historyKeys.join('.') + '.' : ''}${item.translationKey}.title`
              )
            : item?.content && (item.content[this.translate.currentLang] ?? item.content.en);
    }

    getHeadlineTitle(item: IFaq): string {
        return item.translationKey
            ? this.translate.instant(`faq.${this.historyKeys?.length ? this.historyKeys.join('.') : ''}.title`)
            : item.title
              ? item.title[this.translate.currentLang] || item.title
              : (item.title as string);
    }

    getHeadline() {
        return this.historyStack.length
            ? this.getHeadlineTitle(this.currentFaqItem)
            : this.translate.instant('faq.header');
    }

    async handleItem(item: IFaq, attachment: IAttachment, lang?: string) {
        try {
            await this.fileService.getFaqDocument(item.id, attachment.name, lang);
        } catch (e) {
            console.error(e.message);
        }
    }

    private async loadData() {
        this.faqItems = await this.faqService.getFAQ();
        this.currentFaqItem = { translationKey: this.initialFaqHeadline, items: this.faqItems };
        this.visibleItems = this.currentFaqItem.items?.filter(e => !e.isVisible || e.isVisible());
    }
}
